import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import api from "../../api";
import { createAsyncThunkInstance } from "../createAsyncThunkInstance";
import { ICommonInitialState } from "../../shared/types/storeTypes";
import { IAnaliseVacancy, IAnaliseVacancyRequest, IAnaliseVacancyResponse, ICandidateListRequest, ICandidateListResponse, IRecruitersAnalytics, ISingleVacancyAnalise, SourceDetails, IRecruiterAnalyticsRequest } from "../../api/analytics/types";

interface IAnalyticsState extends ICommonInitialState {
  analiseVacancy: IAnaliseVacancy[];
  candidatesModalInfo: ICandidateListResponse | null;
  currentSources: SourceDetails[];
  defaultSources: SourceDetails[];
  vacanciesList: ISingleVacancyAnalise[]
  defaultCount: number | null;
  totalSourceCount: number | null;
  period_from: number | null;
  period_to: number | null;
  status: number | null;
  recruiter: number | null;
  customer: number | null;
  currentVacancyName: string | null;
  currentStatus: string | null;
  currentDepartment: number ;
  recruiterAnalise: IRecruitersAnalytics[]
  currentChannel: number;
  // Сохраненные значения фильтров для сохранения состояния между вкладками
  selectedStatus: string | null;
  selectedRecruiter: string | null;
  selectedCustomer: string | null;
  selectedDepartment: string | null;
  selectedChannel: string | null;
}

// Функции для работы с localStorage
const getStoredFilters = () => {
  try {
    const stored = localStorage.getItem('analytics_filters');
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const saveFiltersToStorage = (filters: any) => {
  try {
    localStorage.setItem('analytics_filters', JSON.stringify(filters));
  } catch {
    // Игнорируем ошибки сохранения
  }
};

const storedFilters = getStoredFilters();

const initialState: IAnalyticsState = {
  analiseVacancy: [],
  candidatesModalInfo: null,
  currentSources: [],
  defaultSources: [],
  vacanciesList: [],
  defaultCount: null,
  totalSourceCount: null,
  period_from: storedFilters.period_from || null,
  period_to: storedFilters.period_to || null,
  status: storedFilters.status || null,
  recruiter: storedFilters.recruiter || null,
  customer: storedFilters.customer || null,
  currentStatus: null,
  currentVacancyName: null,
  currentDepartment: storedFilters.currentDepartment || 0,
  recruiterAnalise: [],
  isLoading: false,
  error: null,
  currentChannel: storedFilters.currentChannel || 0,
  selectedStatus: storedFilters.selectedStatus || null,
  selectedRecruiter: storedFilters.selectedRecruiter || null,
  selectedCustomer: storedFilters.selectedCustomer || null,
  selectedDepartment: storedFilters.selectedDepartment || null,
  selectedChannel: storedFilters.selectedChannel || null,
};

export const fetchAnaliseVacancy = createAsyncThunkInstance<
IAnaliseVacancyResponse,
  IAnaliseVacancyRequest
>("analytics/fetchAnaliseVacancy", api.analise.getAnaliseVacancy);

export const fetchVacanciesList = createAsyncThunkInstance<
  ISingleVacancyAnalise[],
  IAnaliseVacancyRequest
>("analytics/fetchVacanciesList", api.analise.getVacancies);

export const fetchCandidateList = createAsyncThunkInstance<
  ICandidateListResponse,
  ICandidateListRequest
>("analytics/fetchCandidateList", api.analise.getCandidateList);

export const fetchAnaliseRecruiter = createAsyncThunkInstance<
  IRecruitersAnalytics[],
  IRecruiterAnalyticsRequest
>("analytics/fetchAnaliseRecruiter", api.analise.getAnaliseRecruiter);

const analyticsSlice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    setCurrentOffers: (state, action: PayloadAction<SourceDetails[]>) => {
      state.currentSources = action.payload;
    },
    setDefaultSources: (state, action: PayloadAction<any[]>) => {
      state.defaultSources = action.payload;
    },
    setDefaultCount: (state, action: PayloadAction<number>) => {
      state.defaultCount = action.payload;
    },
    resetCurrentOffers: (state) => {
      state.currentSources = [];
    },
    setCurrentOffersCount: (state, action: PayloadAction<number>) => {
      state.totalSourceCount = action.payload;
    },
    setPeriodFrom: (state, action: PayloadAction<number | null>) => {
      state.period_from = action.payload;
      saveFiltersToStorage({
        period_from: state.period_from,
        period_to: state.period_to,
        status: state.status,
        recruiter: state.recruiter,
        customer: state.customer,
        currentDepartment: state.currentDepartment,
        currentChannel: state.currentChannel,
        selectedStatus: state.selectedStatus,
        selectedRecruiter: state.selectedRecruiter,
        selectedCustomer: state.selectedCustomer,
        selectedDepartment: state.selectedDepartment,
        selectedChannel: state.selectedChannel,
      });
    },
    setPeriodTo: (state, action: PayloadAction<number | null>) => {
      state.period_to = action.payload;
      saveFiltersToStorage({
        period_from: state.period_from,
        period_to: state.period_to,
        status: state.status,
        recruiter: state.recruiter,
        customer: state.customer,
        currentDepartment: state.currentDepartment,
        currentChannel: state.currentChannel,
        selectedStatus: state.selectedStatus,
        selectedRecruiter: state.selectedRecruiter,
        selectedCustomer: state.selectedCustomer,
        selectedDepartment: state.selectedDepartment,
        selectedChannel: state.selectedChannel,
      });
    },
    setCurrentStatus: (state, action: PayloadAction<number | null>) => {
      state.status = action.payload;
      saveFiltersToStorage({
        period_from: state.period_from,
        period_to: state.period_to,
        status: state.status,
        recruiter: state.recruiter,
        customer: state.customer,
        currentDepartment: state.currentDepartment,
        currentChannel: state.currentChannel,
        selectedStatus: state.selectedStatus,
        selectedRecruiter: state.selectedRecruiter,
        selectedCustomer: state.selectedCustomer,
        selectedDepartment: state.selectedDepartment,
        selectedChannel: state.selectedChannel,
      });
    },
    setCurrentRecruiter: (state, action: PayloadAction<number | null>) => {
      state.recruiter = action.payload;
      saveFiltersToStorage({
        period_from: state.period_from,
        period_to: state.period_to,
        status: state.status,
        recruiter: state.recruiter,
        customer: state.customer,
        currentDepartment: state.currentDepartment,
        currentChannel: state.currentChannel,
        selectedStatus: state.selectedStatus,
        selectedRecruiter: state.selectedRecruiter,
        selectedCustomer: state.selectedCustomer,
        selectedDepartment: state.selectedDepartment,
        selectedChannel: state.selectedChannel,
      });
    },
    setCurrentCustomer: (state, action: PayloadAction<number | null>) => {
      state.customer = action.payload;
      saveFiltersToStorage({
        period_from: state.period_from,
        period_to: state.period_to,
        status: state.status,
        recruiter: state.recruiter,
        customer: state.customer,
        currentDepartment: state.currentDepartment,
        currentChannel: state.currentChannel,
        selectedStatus: state.selectedStatus,
        selectedRecruiter: state.selectedRecruiter,
        selectedCustomer: state.selectedCustomer,
        selectedDepartment: state.selectedDepartment,
        selectedChannel: state.selectedChannel,
      });
    },
    setCurrentVacancyStatus: (state, action: PayloadAction<string | null>) => {
      state.currentStatus = action.payload;
    },
    setCurrentVacancyName: (state, action: PayloadAction<string | null>) => {
      state.currentVacancyName = action.payload;
    },
    resetCurrentOffersCount: (state) => {
      state.totalSourceCount = null;
    },
    setCurrentDepartment: (state, action: PayloadAction<number>) => {
      state.currentDepartment = action.payload;
      saveFiltersToStorage({
        period_from: state.period_from,
        period_to: state.period_to,
        status: state.status,
        recruiter: state.recruiter,
        customer: state.customer,
        currentDepartment: state.currentDepartment,
        currentChannel: state.currentChannel,
        selectedStatus: state.selectedStatus,
        selectedRecruiter: state.selectedRecruiter,
        selectedCustomer: state.selectedCustomer,
        selectedDepartment: state.selectedDepartment,
        selectedChannel: state.selectedChannel,
      });
    },
    setCurrentChannel: (state, action: PayloadAction<number>) => {
      state.currentChannel = action.payload;
      saveFiltersToStorage({
        period_from: state.period_from,
        period_to: state.period_to,
        status: state.status,
        recruiter: state.recruiter,
        customer: state.customer,
        currentDepartment: state.currentDepartment,
        currentChannel: state.currentChannel,
        selectedStatus: state.selectedStatus,
        selectedRecruiter: state.selectedRecruiter,
        selectedCustomer: state.selectedCustomer,
        selectedDepartment: state.selectedDepartment,
        selectedChannel: state.selectedChannel,
      });
    },
    setSelectedStatus: (state, action: PayloadAction<string | null>) => {
      state.selectedStatus = action.payload;
      saveFiltersToStorage({
        period_from: state.period_from,
        period_to: state.period_to,
        status: state.status,
        recruiter: state.recruiter,
        customer: state.customer,
        currentDepartment: state.currentDepartment,
        currentChannel: state.currentChannel,
        selectedStatus: state.selectedStatus,
        selectedRecruiter: state.selectedRecruiter,
        selectedCustomer: state.selectedCustomer,
        selectedDepartment: state.selectedDepartment,
        selectedChannel: state.selectedChannel,
      });
    },
    setSelectedRecruiter: (state, action: PayloadAction<string | null>) => {
      state.selectedRecruiter = action.payload;
      saveFiltersToStorage({
        period_from: state.period_from,
        period_to: state.period_to,
        status: state.status,
        recruiter: state.recruiter,
        customer: state.customer,
        currentDepartment: state.currentDepartment,
        currentChannel: state.currentChannel,
        selectedStatus: state.selectedStatus,
        selectedRecruiter: state.selectedRecruiter,
        selectedCustomer: state.selectedCustomer,
        selectedDepartment: state.selectedDepartment,
        selectedChannel: state.selectedChannel,
      });
    },
    setSelectedCustomer: (state, action: PayloadAction<string | null>) => {
      state.selectedCustomer = action.payload;
      saveFiltersToStorage({
        period_from: state.period_from,
        period_to: state.period_to,
        status: state.status,
        recruiter: state.recruiter,
        customer: state.customer,
        currentDepartment: state.currentDepartment,
        currentChannel: state.currentChannel,
        selectedStatus: state.selectedStatus,
        selectedRecruiter: state.selectedRecruiter,
        selectedCustomer: state.selectedCustomer,
        selectedDepartment: state.selectedDepartment,
        selectedChannel: state.selectedChannel,
      });
    },
    setSelectedDepartment: (state, action: PayloadAction<string | null>) => {
      state.selectedDepartment = action.payload;
      saveFiltersToStorage({
        period_from: state.period_from,
        period_to: state.period_to,
        status: state.status,
        recruiter: state.recruiter,
        customer: state.customer,
        currentDepartment: state.currentDepartment,
        currentChannel: state.currentChannel,
        selectedStatus: state.selectedStatus,
        selectedRecruiter: state.selectedRecruiter,
        selectedCustomer: state.selectedCustomer,
        selectedDepartment: state.selectedDepartment,
        selectedChannel: state.selectedChannel,
      });
    },
    setSelectedChannel: (state, action: PayloadAction<string | null>) => {
      state.selectedChannel = action.payload;
      saveFiltersToStorage({
        period_from: state.period_from,
        period_to: state.period_to,
        status: state.status,
        recruiter: state.recruiter,
        customer: state.customer,
        currentDepartment: state.currentDepartment,
        currentChannel: state.currentChannel,
        selectedStatus: state.selectedStatus,
        selectedRecruiter: state.selectedRecruiter,
        selectedCustomer: state.selectedCustomer,
        selectedDepartment: state.selectedDepartment,
        selectedChannel: state.selectedChannel,
      });
    },
    resetAnalyticsFilters: (state) => {
      state.period_from = null;
      state.period_to = null;
      state.status = null;
      state.recruiter = null;
      state.customer = null;
      state.currentDepartment = 0;
      state.currentChannel = 0;
      state.selectedStatus = null;
      state.selectedRecruiter = null;
      state.selectedCustomer = null;
      state.selectedDepartment = null;
      state.selectedChannel = null;
      // Очищаем localStorage
      try {
        localStorage.removeItem('analytics_filters');
      } catch {
        // Игнорируем ошибки
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAnaliseVacancy.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAnaliseVacancy.fulfilled, (state, action) => {
        state.isLoading = false;
        state.analiseVacancy = action.payload.stages;
        state.vacanciesList = action.payload.vacancies;
      })
      .addCase(fetchAnaliseRecruiter.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAnaliseRecruiter.fulfilled, (state, action) => {
        state.isLoading = false;
        state.recruiterAnalise = action.payload;
      })
      .addCase(fetchCandidateList.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCandidateList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.candidatesModalInfo = action.payload;
      });
  },
});


export const { 
  setCurrentVacancyName,
  setCurrentVacancyStatus,
  setCurrentCustomer,
  setCurrentRecruiter, 
  setCurrentStatus, 
  setPeriodFrom, 
  setPeriodTo, 
  setDefaultCount, 
  setDefaultSources, 
  setCurrentOffers, 
  resetCurrentOffers, 
  setCurrentOffersCount, 
  resetCurrentOffersCount, 
  setCurrentDepartment, 
  setCurrentChannel,
  setSelectedStatus,
  setSelectedRecruiter,
  setSelectedCustomer,
  setSelectedDepartment,
  setSelectedChannel,
  resetAnalyticsFilters
} = analyticsSlice.actions;

export const analyticsReducer = analyticsSlice.reducer;
